import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import NoteOutlinedIcon from "@mui/icons-material/NoteOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import useTranslation from "../../../shared/hooks/useTranslation";
import { ServiceCompletionDetails } from "../models/executionModels";
import serviceExecutionApi from "../api/serviceExecutionApi";
import { dt } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import dayjs from "dayjs";

interface CompletionFile {
  name: string;
  size: number;
  url?: string;
}

interface ConfirmCompletionDialogProps {
  open: boolean;
  completion: ServiceCompletionDetails | null;
  files?: CompletionFile[];
  onConfirm: () => Promise<void>;
  onRequestRevision: () => void;
  onClose: () => void;
  loading: boolean;
}

const formatCurrency = (value?: number) => {
  if (value == null) return "\u2014";
  return `\u20AC${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return "\u2014";
  return dayjs(dateStr).format("MMM DD, YYYY \u00B7 HH:mm");
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Section header: icon + uppercase bold label (per Dialog Standard)
const SectionHeader: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, mt: 2.5 }}>
    {icon}
    <Box
      sx={{
        fontSize: "12px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "1px",
        color: dt.textPrimary,
        fontFamily: dt.font,
      }}
    >
      {label}
    </Box>
  </Box>
);

// Cost row
const CostRow: React.FC<{
  label: string;
  actual: string;
  estimated: string;
  isBold?: boolean;
}> = ({ label, actual, estimated, isBold }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      ...(isBold && {
        backgroundColor: "rgba(39,174,96,0.06)",
        borderRadius: "6px",
        px: 1.5,
        py: 0.75,
        mt: 0.5,
      }),
    }}
  >
    <Box
      sx={{
        fontSize: "12px",
        color: dt.textSecondary,
        fontWeight: isBold ? 600 : 400,
        fontFamily: dt.font,
        minWidth: "100px",
      }}
    >
      {label}
    </Box>
    <Box sx={{ display: "flex", gap: 3, flex: 1 }}>
      <Box>
        <Box sx={{ fontSize: "10px", color: dt.textMuted, fontFamily: dt.font }}>Actual</Box>
        <Box
          sx={{
            fontSize: "13px",
            fontWeight: isBold ? 700 : 500,
            fontFamily: dt.fontMono,
            color: "#27AE60",
          }}
        >
          {actual}
        </Box>
      </Box>
      <Box>
        <Box sx={{ fontSize: "10px", color: dt.textMuted, fontFamily: dt.font }}>Estimated</Box>
        <Box
          sx={{
            fontSize: "13px",
            fontFamily: dt.fontMono,
            color: dt.textMuted,
          }}
        >
          {estimated}
        </Box>
      </Box>
    </Box>
  </Box>
);

const ConfirmCompletionDialog: React.FC<ConfirmCompletionDialogProps> = ({
  open,
  completion,
  files,
  onConfirm,
  onRequestRevision,
  onClose,
  loading,
}) => {
  useRedesignSetup();
  const { translate } = useTranslation(["servicing", "common"]);
  const isPhone = useMediaQuery("(max-width:600px)");
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);

  // Download a completion file — fetches content from API, converts to data URI, opens in new tab
  const handleFileDownload = async (fileName: string) => {
    if (!completion?.serviceRequestId) return;
    setDownloadingFile(fileName);
    try {
      const apiFiles = await serviceExecutionApi.getCompletionFiles(completion.serviceRequestId);
      const match = apiFiles.find((f) => f.name === fileName);
      if (!match || !match.content) {
        setDownloadingFile(null);
        return;
      }
      // Build data URI from base64 content
      const ext = fileName.split(".").pop()?.toLowerCase() || "";
      const mimeTypes: Record<string, string> = {
        pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg",
        gif: "image/gif", webp: "image/webp", doc: "application/msword",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        xls: "application/vnd.ms-excel", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        csv: "text/csv", txt: "text/plain",
      };
      const mime = mimeTypes[ext] || "application/octet-stream";
      const content = match.content.startsWith("data:") || match.content.startsWith("http")
        ? match.content
        : `data:${mime};base64,${match.content}`;
      const link = document.createElement("a");
      link.href = content;
      link.download = fileName;
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to download completion file:", err);
    } finally {
      setDownloadingFile(null);
    }
  };

  // Inject dialog-specific keyframes
  useEffect(() => {
    const styleId = "confirm-completion-dialog-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes confirmCompletionSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  if (!completion) return null;

  const hasCostComparison =
    completion.actualLaborHours != null ||
    completion.actualPartsCost != null ||
    completion.finalCost != null;

  const hasFiles = files && files.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          overflow: "hidden",
          fontFamily: dt.font,
          "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
          animation: "confirmCompletionSlideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          ...(isPhone && {
            margin: "16px",
            width: "calc(100% - 32px)",
            maxWidth: "none",
          }),
        },
      }}
    >
      {/* HEADER — icon next to title on one line (per ConfirmAppointment reference) */}
      <DialogTitle sx={{ textAlign: "center", pt: 3, pb: 0.5, px: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
          {/* Green checkmark circle */}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "#E8F8F0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 22, color: "#27AE60" }} />
          </Box>
          {/* Title */}
          <Box
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              fontFamily: dt.font,
              color: dt.textPrimary,
              letterSpacing: "-0.3px",
            }}
          >
            {translate("servicing:completion.confirmTitle")}
          </Box>
        </Box>
      </DialogTitle>

      {/* CONTENT */}
      <DialogContent sx={{ px: 4, pt: 1, pb: 2 }}>
        {/* Subtitle — incorporates provider name naturally */}
        <Box
          sx={{
            fontSize: "13.5px",
            fontFamily: dt.font,
            color: dt.textSecondary,
            textAlign: "center",
            lineHeight: 1.5,
            mb: 2.5,
          }}
        >
          {translate("servicing:completion.confirmDescription")}
          {completion.providerName && (
            <>
              {" \u2014 "}
              <Box component="span" sx={{ fontWeight: 600, color: dt.textPrimary }}>
                {completion.providerName}
              </Box>
            </>
          )}
        </Box>

        {/* COMPLETION SUMMARY */}
        <SectionHeader
          icon={<DescriptionOutlinedIcon sx={{ fontSize: 18, color: dt.textSecondary }} />}
          label={translate("servicing:completion.summary")}
        />
        <Box
          sx={{
            backgroundColor: dt.inputBg,
            borderRadius: dt.radiusSm,
            padding: "14px 16px",
            fontSize: "13px",
            color: dt.textPrimary,
            fontFamily: dt.font,
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
          }}
        >
          {completion.completionSummary}
        </Box>

        {/* COST COMPARISON */}
        {hasCostComparison && (
          <>
            <SectionHeader
              icon={<AttachMoneyOutlinedIcon sx={{ fontSize: 18, color: dt.textSecondary }} />}
              label={translate("servicing:completion.costComparison")}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {(completion.actualLaborHours != null || completion.estimatedLaborHours != null) && (
                <CostRow
                  label={translate("servicing:completion.laborHours")}
                  actual={completion.actualLaborHours != null ? `${completion.actualLaborHours}h` : "\u2014"}
                  estimated={completion.estimatedLaborHours != null ? `${completion.estimatedLaborHours}h` : "\u2014"}
                />
              )}
              {(completion.actualPartsCost != null || completion.estimatedPartsCost != null) && (
                <CostRow
                  label={translate("servicing:completion.partsCost")}
                  actual={formatCurrency(completion.actualPartsCost)}
                  estimated={formatCurrency(completion.estimatedPartsCost)}
                />
              )}
              <CostRow
                label={translate("servicing:completion.totalCost")}
                actual={formatCurrency(completion.finalCost)}
                estimated={formatCurrency(completion.estimatedTotalCost)}
                isBold
              />
            </Box>
          </>
        )}

        {/* ATTACHMENTS — clickable, downloads via API */}
        {hasFiles && (
          <>
            <SectionHeader
              icon={<AttachFileOutlinedIcon sx={{ fontSize: 18, color: dt.textSecondary }} />}
              label={translate("servicing:completion.attachments")}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {files.map((file, idx) => {
                const isDownloading = downloadingFile === file.name;
                return (
                  <Box
                    key={idx}
                    onClick={() => {
                      if (file.url) {
                        window.open(file.url, "_blank", "noopener,noreferrer");
                      } else {
                        handleFileDownload(file.name);
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      border: `1px solid ${dt.cardBorder}`,
                      borderRadius: dt.radiusSm,
                      padding: "10px 14px",
                      backgroundColor: dt.inputBg,
                      cursor: isDownloading ? "wait" : "pointer",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        backgroundColor: dt.blueSoft,
                        borderColor: dt.blue,
                      },
                    }}
                  >
                    {isDownloading ? (
                      <CircularProgress size={18} sx={{ color: dt.blue, flexShrink: 0 }} />
                    ) : (
                      <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, color: dt.blue, flexShrink: 0 }} />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: dt.textLink || dt.blue,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "block",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {file.name}
                      </Box>
                      <Box sx={{ fontSize: "11px", fontFamily: dt.fontMono, color: dt.textMuted }}>
                        {formatFileSize(file.size)}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        )}

        {/* PROVIDER NOTES */}
        {completion.providerNotes && (
          <>
            <SectionHeader
              icon={<NoteOutlinedIcon sx={{ fontSize: 18, color: dt.textSecondary }} />}
              label={translate("servicing:completion.providerNotes")}
            />
            <Box
              sx={{
                backgroundColor: dt.inputBg,
                borderRadius: dt.radiusSm,
                padding: "12px 16px",
                fontSize: "13px",
                color: dt.textPrimary,
                fontFamily: dt.font,
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
              }}
            >
              {completion.providerNotes}
            </Box>
          </>
        )}

        {/* PREVIOUS REVISION INFO */}
        {completion.revisionRequestedDate && (
          <>
            <Box sx={{ mt: 2.5 }}>
              <Box
                sx={{
                  backgroundColor: "#FFF8E1",
                  border: "1px solid #FFE082",
                  borderLeft: "3px solid #FF9800",
                  borderRadius: dt.radiusSm,
                  padding: "12px 16px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: "6px" }}>
                  <WarningAmberOutlinedIcon sx={{ fontSize: 14, color: "#E65100" }} />
                  <Box sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: "#E65100", fontFamily: dt.font }}>
                    {translate("servicing:completion.previousRevision")}
                  </Box>
                </Box>
                <Box sx={{ fontSize: "13px", color: "#BF360C", fontFamily: dt.font, lineHeight: 1.5 }}>
                  {completion.revisionReason}
                </Box>
                <Box sx={{ fontSize: "11px", color: dt.textMuted, fontFamily: dt.font, mt: "4px" }}>
                  {formatDate(completion.revisionRequestedDate)}
                </Box>
              </Box>
            </Box>
          </>
        )}

        {/* COMPLETION DATE — with EventOutlined icon */}
        <SectionHeader
          icon={<EventOutlinedIcon sx={{ fontSize: 18, color: dt.textSecondary }} />}
          label={translate("servicing:completion.completedOn")}
        />
        <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <Box>
            <Box sx={{ fontSize: "13px", fontWeight: 500, color: dt.textPrimary, fontFamily: dt.fontMono }}>
              {formatDate(completion.completedByProviderDate)}
            </Box>
          </Box>
          {completion.confirmedByOwnerDate && (
            <Box>
              <Box sx={{ fontSize: "10px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.8px", color: dt.textMuted, fontFamily: dt.font, mb: "2px" }}>
                {translate("servicing:completion.confirmedOn")}
              </Box>
              <Box sx={{ fontSize: "13px", fontWeight: 500, color: dt.textPrimary, fontFamily: dt.fontMono }}>
                {formatDate(completion.confirmedByOwnerDate)}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* ACTIONS — no footer divider, centered per Dialog Standard */}
      <DialogActions
        sx={{
          px: 4,
          pb: 3,
          pt: 1.5,
          justifyContent: "center",
          gap: 1.5,
          flexDirection: isPhone ? "column-reverse" : "row",
          "& > :not(:first-of-type)": {
            ml: isPhone ? "0 !important" : undefined,
          },
        }}
      >
        {/* Cancel — text button */}
        <Button
          onClick={onClose}
          sx={{
            fontFamily: dt.font,
            fontSize: "14px",
            fontWeight: 600,
            textTransform: "none",
            color: dt.blue,
            padding: "8px 24px",
            borderRadius: dt.radiusSm,
            width: isPhone ? "100%" : "auto",
            "&:hover": { backgroundColor: dt.blueSoft },
          }}
        >
          {translate("common:buttons.cancel")}
        </Button>

        {/* Request Revision — outlined with icon */}
        <Button
          onClick={onRequestRevision}
          variant="outlined"
          startIcon={<EditOutlinedIcon sx={{ fontSize: "18px !important" }} />}
          sx={{
            fontFamily: dt.font,
            fontSize: "14px",
            fontWeight: 600,
            textTransform: "none",
            borderColor: dt.cardBorder,
            color: dt.textSecondary,
            padding: "10px 28px",
            borderRadius: dt.radiusSm,
            boxShadow: "none",
            width: isPhone ? "100%" : "auto",
            whiteSpace: "nowrap",
            "&:hover": {
              borderColor: "#F59E0B",
              color: "#D97706",
              backgroundColor: "rgba(245,158,11,0.04)",
            },
          }}
        >
          {translate("servicing:completion.requestRevision")}
        </Button>

        {/* Confirm Completion — filled blue with icon (per Dialog Standard) */}
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={20} sx={{ color: "#FFFFFF" }} />
            ) : (
              <CheckCircleOutlineIcon sx={{ fontSize: "18px !important" }} />
            )
          }
          sx={{
            fontFamily: dt.font,
            fontSize: "14px",
            fontWeight: 600,
            textTransform: "none",
            backgroundColor: dt.blue,
            padding: "10px 28px",
            borderRadius: dt.radiusSm,
            boxShadow: "none",
            width: isPhone ? "100%" : "auto",
            whiteSpace: "nowrap",
            "&:hover": {
              backgroundColor: dt.blueHover,
              boxShadow: "0 2px 8px rgba(52,152,219,0.3)",
            },
            "&.Mui-disabled": {
              backgroundColor: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          {loading
            ? translate("servicing:completion.confirming")
            : translate("servicing:completion.confirmCompletion")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmCompletionDialog;
